import React, { useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { getSessionId } from '../../utils/sessionId';
import { getSelectedDineInTableId } from '../../utils/tableSelection';
import { getActiveServerId } from '../../utils/staffSession';
import { toast } from 'react-toastify';

/**
 * Weight entry for a "Price by Weight" product - staff type the reading off the shop's
 * standalone scale, the POS computes price = weight / pricing_unit_denominator * price_per_unit
 * (same arithmetic the backend has always used for per-weight pricing), then adds it to the
 * cart through the EXACT same "custom item" mechanism TakeawayCart.js's existing
 * handleAddCustomItem already uses (ADD_TO_CART_DETAIL with is_custom_item=1, cust_product_name/
 * cust_product_price overriding the placeholder product's own name/price) - so the rest of
 * cart/checkout/kitchen-printing is completely unchanged.
 */
const EnterWeightModal = ({ isOpen, product, placeholderItemId, orderType, editOrderId, onClose, onAdded }) => {
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setWeight('');
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const weightNum = parseFloat(weight);
  const isValidWeight = weight !== '' && !isNaN(weightNum) && weightNum > 0;
  const total = isValidWeight
    ? (weightNum / product.pricing_unit_denominator) * product.price_per_unit
    : 0;

  const handleAdd = async () => {
    if (!isValidWeight) {
      toast.error('Enter a valid weight');
      return;
    }
    if (!placeholderItemId) {
      toast.error('Price by Weight is not set up yet - add a weighted product first');
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('qty', 1);
      payload.append('type', 'updated_price');
      payload.append('default_ingredients_quantity', JSON.stringify([]));
      payload.append('add_on_ingredients_quantity', JSON.stringify([]));
      payload.append('id', placeholderItemId);
      payload.append('cust_product_name', `${product.name} (${weight}${product.weight_unit})`);
      payload.append('cust_product_price', total.toFixed(2));
      payload.append('is_custom_item', 1);
      payload.append('session_id', getSessionId());

      const phoneOrder = orderType === 'takeaway' || orderType === 'phone' ? 1 : 0;
      payload.append('phone_order', phoneOrder);
      payload.append('selected_customer_id', 1);

      const activeServerId = getActiveServerId();
      if (activeServerId) {
        payload.append('added_by_employee_id', activeServerId);
      }
      if (editOrderId) {
        payload.append('edit_order_id', editOrderId);
      }
      if (orderType === 'dinein') {
        const tableId = getSelectedDineInTableId();
        if (tableId) {
          payload.append('table_id', tableId);
        }
      }

      await callAPI(apiUrls.ADD_TO_CART_DETAIL, {}, 'POST', payload, {});
      toast.success('Added to order');
      onAdded();
    } catch (err) {
      console.error('Failed to add weighted product to cart', err);
      toast.error('Failed to add item to cart');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose}>
      <div className="checkinFormCard">
        <h4 className="lgTitle pb-2 text-center">{product.name}</h4>
        <p className="text-center mb-3" style={{ opacity: 0.75 }}>
          ${Number(product.price_per_unit).toFixed(2)} / {product.pricing_unit_denominator}{product.weight_unit}
        </p>

        <div className="formStyle">
          <div className="row gx-2 gy-3">
            <div className="col-12">
              <label className="form-label">Enter Weight ({product.weight_unit})</label>
              <input
                type="number"
                step="0.01"
                min="0"
                autoFocus
                className="form-control"
                placeholder={`Weight in ${product.weight_unit}`}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>

            <div className="col-12 text-center">
              <span style={{ fontSize: '20px', fontWeight: 700 }}>
                Total: ${total.toFixed(2)}
              </span>
            </div>

            <div className="col-12 text-center pt-2">
              <button className="btn btn-secondary me-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn iconBtn btnPrimary"
                onClick={handleAdd}
                disabled={saving || !isValidWeight}
              >
                <span>{saving ? 'Adding...' : 'Add to Order'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default EnterWeightModal;
